import { useState } from 'react'

import { cupLevel, ru } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Своя мензурка (урок 10): в стаканчик шприцем доливают по 20 мл и ставят метку против уровня.
 * У цилиндра метки ложатся через равные промежутки; у одноразового стаканчика, который
 * расширяется кверху, — всё теснее. Поэтому шкалу размечают порциями, а не линейкой.
 */

const ПОРЦИЯ = 20 // мл
const H = 9 // см, высота сосуда
const PX = 24 // пикселей на см
const ДНО = 240
const СОСУДЫ = [
  { имя: 'стаканчик', r0: 2.2, r1: 3.4 },
  { имя: 'цилиндр', r0: 2.8, r1: 2.8 },
] as const

export default function CupScaleLab() {
  const [сосуд, setСосуд] = useState(0)
  const [налито, setНалито] = useState(0)

  const с = СОСУДЫ[сосуд]
  const ёмкость = Math.floor((Math.PI * H * (с.r0 ** 2 + с.r0 * с.r1 + с.r1 ** 2)) / 3 / ПОРЦИЯ) * ПОРЦИЯ
  const h = cupLevel(налито, с.r0, с.r1, H)
  const метки = Array.from({ length: налито / ПОРЦИЯ }, (_, i) => cupLevel((i + 1) * ПОРЦИЯ, с.r0, с.r1, H))
  const cx = 130
  const r = (hh: number) => (с.r0 + ((с.r1 - с.r0) * hh) / H) * PX
  const y = (hh: number) => ДНО - hh * PX
  const стенки = `M${cx - r(H)} ${y(H)} L${cx - r(0)} ${ДНО} L${cx + r(0)} ${ДНО} L${cx + r(H)} ${y(H)}`
  const шаги = метки.map((m, i) => (i === 0 ? m : m - метки[i - 1]))

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" style={{ maxWidth: 260, margin: '0 auto' }} viewBox="0 0 230 260" role="img" aria-label="Стаканчик с метками">
          {налито > 0 && (
            <polygon points={`${cx - r(h)},${y(h)} ${cx - r(0)},${ДНО} ${cx + r(0)},${ДНО} ${cx + r(h)},${y(h)}`} className="fill-earth" />
          )}
          <path d={стенки} className="ink" fill="none" strokeWidth={2} />
          {метки.map((m, i) => (
            <g key={i}>
              <line x1={cx + r(m) - 2} y1={y(m)} x2={cx + r(m) + 14} y2={y(m)} className="ink-heavy" strokeWidth={1.6} />
              <text x={cx + r(m) + 18} y={y(m) + 4} className="lab-tick">{(i + 1) * ПОРЦИЯ}</text>
            </g>
          ))}
        </svg>
        <div>
          <div className="lab-readout lab-readout-col">
            <span className="el">налито {налито} мл</span>
            <span>
              промежутки между метками:{' '}
              {шаги.length ? шаги.map((ш) => `${ru(ш * 10, 0)} мм`).join(', ') : '—'}
            </span>
          </div>
          <div className="row-wrap" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" disabled={налито + ПОРЦИЯ > ёмкость} onClick={() => setНалито((x) => x + ПОРЦИЯ)}>
              💉 Долить {ПОРЦИЯ} мл и поставить метку
            </button>
            <button className="btn" onClick={() => setНалито(0)}>вылить</button>
          </div>
        </div>
      </div>

      <Predict
        question="Стаканчик расширяется кверху. Какими будут промежутки между метками через каждые 20 мл?"
        options={['одинаковыми', 'всё шире кверху', 'всё теснее кверху']}
        right={2}
        revealed={сосуд === 0 && налито >= 100}
        explain="Наверху стакан шире: те же 20 мл растекаются тоньше, уровень поднимается меньше. Поэтому шкалу размечают порциями воды, а не по линейке."
      />

      <div className="lab-sub">Сосуд</div>
      <Choice options={СОСУДЫ.map((x, i) => ({ value: i, label: x.имя }))} value={сосуд} onChange={(v) => { setСосуд(v); setНалито(0) }} />
      <p className="muted small">
        Так в уроке делали мензурку из одноразового стаканчика: шприц на 20 мл, полоска бумаги, карандаш.
        Пределы измерения 20–140 мл, цена деления 20 мл. Полоску ставят нижним краем на стол — от него и
        отсчитываются метки.
      </p>
    </div>
  )
}
